import React, { useEffect, useState, forwardRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setAdminConfigModal,
    getBankAptitudesOnCreation,
    setAddElementModalVisibility,
    reorderPublicForm,
    setRespPublicFrom,
    hidePublicCatSub,
    hidePublicAptitudes,
} from "./vcompSlice";
import Langs from "../../app/lang/langs";
import { confirmDialog } from "../popups/ConfirmDialog";
import { showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import MobileDragTree from "./createProjectModalComponents/mobile/MobileDragTree";

// MUI
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

// Confirm dialog

const ORANGE = "#FF9015";

const SlideUp = forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAdminFormConfig() {
    const dispatch = useDispatch();

    const visible = useSelector((state) => state.vcomp.adminConfigModal);
    const bankAptitudesOnCreation = useSelector((state) => state.vcomp.bankAptitudesOnCreation);
    const respPublicFrom = useSelector((state) => state.vcomp.respPublicFrom);

    const [nodes, setNodes] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState({});

    // Fetch data when modal opens
    useEffect(() => {
        if (visible) {
            dispatch(getBankAptitudesOnCreation());
        }
    }, [visible]);

    // Process bank aptitudes into tree nodes
    useEffect(() => {
        let data = [];
        if (bankAptitudesOnCreation && bankAptitudesOnCreation.categories) {
            const processNode = (node) => {
                const keyPrefix =
                    node.scope === "category" ? "cat" :
                    node.scope === "subcategory" ? "subcat" : "apt";
                const key = `${keyPrefix}-${node.id}`;
                return {
                    key,
                    id: node.id,
                    label: node.nom,
                    coef: node.coef,
                    scope: node.scope,
                    className: node.scope,
                    pos: node.pos,
                    visibility: node.visibility,
                    description: node.description,
                    type: node.type,
                    aspect: node.aspect || null,
                    children: node.children ? node.children.map((child) => processNode(child)) : [],
                };
            };
            data = bankAptitudesOnCreation.categories.map((category) => processNode(category));

            // Auto-expand first level
            const keys = {};
            data.forEach((n) => { keys[n.key] = true; });
            setExpandedKeys(keys);
        }
        setNodes(data);
    }, [bankAptitudesOnCreation]);

    // Handle API responses
    useEffect(() => {
        if (respPublicFrom != null) {
            if (respPublicFrom.trim().toLowerCase().includes("successfully")) {
                dispatch(getBankAptitudesOnCreation());
                showSuccessToast(respPublicFrom);
            } else {
                showErrorToast(respPublicFrom);
            }
            dispatch(setRespPublicFrom(null));
        }
    }, [respPublicFrom]);

    const handleClose = () => {
        dispatch(setAdminConfigModal(false));
    };

    // Helper: check if a key appears as a child (not root)
    function isChild(objects, searchKey) {
        function searchChildren(children) {
            return children.some((child) => {
                if (child.key === searchKey) return true;
                if (child.children && child.children.length > 0) return searchChildren(child.children);
                return false;
            });
        }
        return objects.some((obj) => {
            if (obj.children && obj.children.length > 0) return searchChildren(obj.children);
            return false;
        });
    }

    // Drag-drop with validation
    const dragDropAptitudes = useCallback((event) => {
        const { value, dragNode, dropNode } = event;
        if (dropNode) {
            if (dropNode.key.trim().toLowerCase().includes("apt")) return;
        }
        const hasCategoryChild = dragNode.children && dragNode.children.some((child) =>
            child.key.trim().toLowerCase().includes("cat")
        );
        if (hasCategoryChild && dropNode) return;
        if (
            dropNode &&
            isChild(value, dropNode.key) &&
            dropNode.key.trim().toLowerCase().includes("cat") &&
            !dragNode.key.trim().toLowerCase().includes("apt")
        ) return;
        setNodes(value);
    }, []);

    // Expand / collapse all
    const expandAll = useCallback(() => {
        const keys = {};
        const recurse = (list) => {
            list.forEach((n) => {
                if (n.children && n.children.length > 0) {
                    keys[n.key] = true;
                    recurse(n.children);
                }
            });
        };
        recurse(nodes);
        setExpandedKeys(keys);
    }, [nodes]);

    const collapseAll = useCallback(() => {
        setExpandedKeys({});
    }, []);

    // Save reorder
    const handleSave = () => {
        const categories = [];
        const aptitudes = [];
        nodes.forEach((node, index) => {
            if (node.key.trim().toLowerCase().includes("cat")) {
                let idParent = node.id;
                categories.push({ id: node.id, pos: index });
                if (node.children && node.children.length > 0) {
                    node.children.forEach((childCat, idx) => {
                        if (childCat.key.trim().toLowerCase().includes("cat")) {
                            categories.push({ id: childCat.id, id_parent: idParent, pos: idx });
                            if (childCat.children && childCat.children.length > 0) {
                                childCat.children.forEach((childApt, aptIdx) => {
                                    if (childApt.key.trim().toLowerCase().includes("apt")) {
                                        aptitudes.push({ id: childApt.id, id_category: childCat.id, pos: aptIdx });
                                    }
                                });
                            }
                        }
                        if (childCat.key.trim().toLowerCase().includes("apt")) {
                            aptitudes.push({ id: childCat.id, id_category: idParent, pos: idx });
                        }
                    });
                }
            }
            if (node.key.trim().toLowerCase().includes("apt")) {
                aptitudes.push({ id: node.id, id_category: null, pos: index });
            }
        });
        dispatch(reorderPublicForm({ categories, aptitudes }));
    };

    // "Delete" is the second button, not a dismissal: closing the dialog does neither.
    const confirmDeleteCatSub = (id) => {
        confirmDialog({
            message: <Langs str="DELETE_ASSOCIATED" />,
            confirmLabel: <Langs str="KEEP" />,
            cancelLabel: <Langs str="DELETE" />,
            onConfirm: () => dispatch(hidePublicCatSub({ keep: true, id_category: id })),
            onCancel: () => dispatch(hidePublicCatSub({ keep: false, id_category: id })),
        });
    };

    const confirmDeleteApt = (id) => {
        confirmDialog({
            message: <Langs str="DELETE_APTITUDE_CONFIRM_PUBLIC" />,
            danger: true,
            confirmLabel: <Langs str="DELETE" />,
            onConfirm: () => dispatch(hidePublicAptitudes({ id_aptitude: id })),
        });
    };

    // Node double-click => edit
    const handleNodeDoubleClick = useCallback(
        (e) => {
            if (e.node.visibility || e.node.scope === "aptitude") {
                let subcat = isChild(nodes, e.node.key);
                dispatch(
                    setAddElementModalVisibility({
                        visibility: true,
                        element: e.node,
                        subcategory: subcat,
                        isEdit: 1,
                        isAdmin: 1,
                    })
                );
            }
        },
        [nodes, dispatch]
    );

    // Node template for each tree row
    const nodeTemplate = useCallback(
        (node) => {
            let subcat = isChild(nodes, node.key);
            let type = node ? node.key.split("-")[0] : null;
            const isAptitude = node.className === "aptitude";

            return (
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                    {/* Label + badges */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <Typography
                            variant="body2"
                            noWrap
                            sx={{
                                fontWeight: type === "cat" ? 600 : 400,
                                fontSize: "0.88rem",
                                color: "inherit",
                            }}
                        >
                            {node.label}
                        </Typography>
                        {node.aspect === "theoretical" && (
                            <Chip label="T" size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, backgroundColor: "rgba(33,150,243,0.12)", color: "#1976d2" }} />
                        )}
                        {node.aspect === "practical" && (
                            <Chip label="P" size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, backgroundColor: "rgba(76,175,80,0.12)", color: "#2e7d32" }} />
                        )}
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
                        {node.visibility === 1 && (
                            <IconButton
                                size="small"
                                onClick={() =>
                                    dispatch(
                                        setAddElementModalVisibility({
                                            visibility: true,
                                            element: node,
                                            subcategory: subcat,
                                            isEdit: 1,
                                            isAdmin: 1,
                                        })
                                    )
                                }
                                sx={{ width: 34, height: 34, color: "#6b7280" }}
                            >
                                <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        )}
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (type === "apt") confirmDeleteApt(node.id);
                                else confirmDeleteCatSub(node.id);
                            }}
                            sx={{ width: 34, height: 34, color: "#dc2626" }}
                        >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        {!isAptitude && (
                            <IconButton
                                size="small"
                                onClick={() =>
                                    dispatch(
                                        setAddElementModalVisibility({
                                            visibility: true,
                                            element: node,
                                            subcategory: subcat,
                                            isEdit: 0,
                                            isAdmin: 1,
                                        })
                                    )
                                }
                                sx={{ width: 34, height: 34, color: ORANGE }}
                            >
                                <AddIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            );
        },
        [nodes, dispatch]
    );

    const position = nodes?.length || 0;

    return (
        <>
            <Dialog
                open={visible || false}
                onClose={handleClose}
                fullScreen
                TransitionComponent={SlideUp}
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        backgroundColor: "#f8f9fb",
                    },
                }}
            >
                {/* AppBar */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        backgroundColor: "#fff",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                    }}
                >
                    <Toolbar sx={{ minHeight: 56, px: 1.5 }}>
                        <IconButton edge="start" onClick={handleClose} sx={{ color: "#333" }}>
                            <CloseIcon />
                        </IconButton>
                        <Typography
                            variant="h6"
                            sx={{
                                flex: 1,
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                color: "#111318",
                                ml: 0.5,
                            }}
                        >
                            <Langs str="General Form Config" />
                        </Typography>
                        <IconButton
                            onClick={() =>
                                dispatch(
                                    setAddElementModalVisibility({
                                        visibility: true,
                                        element: null,
                                        subcategory: false,
                                        pos: position,
                                        isAdmin: 1,
                                        isEdit: 0,
                                    })
                                )
                            }
                            sx={{
                                color: "#fff",
                                backgroundColor: ORANGE,
                                "&:hover": { backgroundColor: "#e67e00" },
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                boxShadow: "0 2px 6px rgba(255,144,21,0.35)",
                            }}
                        >
                            <AddIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* Actions bar */}
                <Box
                    sx={{
                        px: 2,
                        py: 1.25,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: "#fff",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        flexShrink: 0,
                    }}
                >
                    <Chip
                        icon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Expand All" />}
                        variant="outlined"
                        onClick={expandAll}
                        sx={{
                            height: 32,
                            fontSize: "0.78rem",
                            fontWeight: 500,
                            borderRadius: "8px",
                            borderColor: "rgba(0,0,0,0.12)",
                            "& .MuiChip-icon": { ml: 0.5 },
                        }}
                    />
                    <Chip
                        icon={<UnfoldLessIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Collapse All" />}
                        variant="outlined"
                        onClick={collapseAll}
                        sx={{
                            height: 32,
                            fontSize: "0.78rem",
                            fontWeight: 500,
                            borderRadius: "8px",
                            borderColor: "rgba(0,0,0,0.12)",
                            "& .MuiChip-icon": { ml: 0.5 },
                        }}
                    />
                </Box>

                {/* Tree */}
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <MobileDragTree
                        value={nodes}
                        expandedKeys={expandedKeys}
                        onToggle={setExpandedKeys}
                        nodeTemplate={nodeTemplate}
                        onNodeDoubleClick={handleNodeDoubleClick}
                        onDragDrop={dragDropAptitudes}
                        filter={true}
                        filterPlaceholder="Search..."
                    />
                </Box>

                {/* Sticky bottom buttons */}
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        display: "flex",
                        gap: 1.5,
                        px: 2.5,
                        py: 2,
                        pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                        background: "linear-gradient(to top, rgba(255,255,255,0.95) 60%, rgba(255,255,255,0.8) 100%)",
                        backdropFilter: "blur(18px) saturate(1.6)",
                        WebkitBackdropFilter: "blur(18px) saturate(1.6)",
                        borderTop: "1px solid rgba(255, 144, 21, 0.15)",
                        boxShadow: "0 -4px 24px rgba(255, 144, 21, 0.06), 0 -1px 6px rgba(0, 0, 0, 0.04)",
                        zIndex: 10,
                    }}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius: "14px",
                            textTransform: "none",
                            minHeight: 50,
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            letterSpacing: "-0.01em",
                            border: "1.5px solid rgba(0, 0, 0, 0.12)",
                            color: "#4b5263",
                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                            transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                borderColor: "rgba(255, 144, 21, 0.35)",
                                backgroundColor: "rgba(255, 144, 21, 0.06)",
                                color: ORANGE,
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="CLOSE" />
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSave}
                        startIcon={<SaveIcon sx={{ fontSize: "18px !important" }} />}
                        sx={{
                            borderRadius: "14px",
                            textTransform: "none",
                            minHeight: 50,
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            letterSpacing: "-0.01em",
                            background: "linear-gradient(135deg, #FF9015 0%, #FFA940 100%)",
                            backgroundColor: ORANGE,
                            color: "#fff",
                            border: "none",
                            boxShadow:
                                "0 4px 16px rgba(255, 144, 21, 0.40), 0 8px 32px rgba(255, 144, 21, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                            transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #FF9015 0%, #FFB74D 100%)",
                                backgroundColor: ORANGE,
                                boxShadow:
                                    "0 6px 24px rgba(255, 144, 21, 0.50), 0 12px 40px rgba(255, 144, 21, 0.25)",
                                transform: "translateY(-2px)",
                            },
                            "&:active": {
                                transform: "scale(0.98)",
                                boxShadow: "0 2px 10px rgba(255, 144, 21, 0.35)",
                            },
                        }}
                    >
                        <Langs str="SAVE" />
                    </Button>
                </Box>
            </Dialog>

        </>
    );
}
